import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, Search } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api';

const FLAGS = [['rm', 'Reporting manager'], ['hr', 'HR team'], ['pm', 'Project PM'], ['pd', 'Project Director']];
const TEAM_KEYS = ['procurement', 'finance', 'hr', 'hse', 'quality'];

function Chips({ options, selected, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map(o => {
        const on = selected.includes(o.value);
        return (
          <button key={o.value} type="button" onClick={() => onChange(on ? selected.filter(x => x !== o.value) : [...selected, o.value])}
            className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${on ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:border-[#3b4872]'}`}
            data-testid={`${testid}-${o.value}`}>{o.label}</button>
        );
      })}
    </div>
  );
}

function RuleRow({ k, row, labels, depts, onChange }) {
  const set = (f, v) => onChange({ ...row, [f]: v });
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`ar-row-${k}`}>
      <td className="px-3 py-2 text-xs font-semibold text-slate-700 w-64">{row.label}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
          {FLAGS.map(([f, l]) => (
            <label key={f} className="flex items-center gap-1"><input type="checkbox" checked={!!row[f]} onChange={e => set(f, e.target.checked)} data-testid={`ar-${k}-${f}`} />{l}</label>
          ))}
          {k === 'petty_cash' && <label className="flex items-center gap-1"><input type="checkbox" checked={!!row.petty_approver} onChange={e => set('petty_approver', e.target.checked)} data-testid={`ar-${k}-petty`} />Assigned approver</label>}
          {k === 'po' && <label className="flex items-center gap-1"><input type="checkbox" checked={!!row.matrix} onChange={e => set('matrix', e.target.checked)} data-testid={`ar-${k}-matrix`} />Approval Matrix tier</label>}
          {row.stage && <span className="text-slate-500">RM → HR → Finance stages</span>}
          <span className="text-slate-400" title="Always included">✓ Senior Management · ✓ Super admin</span>
        </div>
      </td>
      <td className="px-3 py-2 space-y-1">
        <Chips options={TEAM_KEYS.map(t => ({ value: t, label: labels[t] }))} selected={row.teams || []} onChange={v => set('teams', v)} testid={`ar-${k}-teams`} />
        <select value="" onChange={e => e.target.value && set('departments', [...(row.departments || []), e.target.value])}
          className="border border-slate-300 rounded-sm text-[11px] px-1 py-0.5" data-testid={`ar-${k}-dept-add`}>
          <option value="">+ department…</option>
          {depts.filter(d => !(row.departments || []).includes(d)).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {(row.departments || []).map(d => (
          <span key={d} className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 bg-amber-50 text-amber-800 rounded-full text-[10px]">{d}
            <button onClick={() => set('departments', row.departments.filter(x => x !== d))}>×</button></span>
        ))}
      </td>
    </tr>
  );
}

function TeamsCard({ rules, setRules, labels, depts }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="ar-teams-card">
      <div className="font-display font-bold text-sm mb-2">Team lists &amp; project gate</div>
      {TEAM_KEYS.map(t => (
        <div key={t} className="flex flex-wrap items-center gap-2 py-1.5 border-t border-slate-100 first:border-0">
          <div className="w-36 text-xs font-semibold">{labels[t]}</div>
          <Chips options={(rules.teams[t] || []).map(d => ({ value: d, label: `${d} ×` }))}
            selected={rules.teams[t] || []} onChange={v => setRules({ ...rules, teams: { ...rules.teams, [t]: v } })} testid={`ar-team-${t}`} />
          <select value="" onChange={e => e.target.value && setRules({ ...rules, teams: { ...rules.teams, [t]: [...(rules.teams[t] || []), e.target.value] } })}
            className="border border-slate-300 rounded-sm text-[11px] px-1 py-0.5" data-testid={`ar-team-${t}-add`}>
            <option value="">+ department…</option>
            {depts.filter(d => !(rules.teams[t] || []).some(x => x.toLowerCase() === d.toLowerCase())).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <label className="ml-auto flex items-center gap-1 text-[11px]">
            <input type="checkbox" checked={!!rules.team_gate[t]} onChange={e => setRules({ ...rules, team_gate: { ...rules.team_gate, [t]: e.target.checked } })} data-testid={`ar-gate-${t}`} />
            Limited to their projects
          </label>
        </div>
      ))}
      <div className="text-[10px] text-slate-500 mt-1">The Procurement list is the same list as the Procurement approver group.</div>
    </div>
  );
}

function CheckUser() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_id: '', type: 'mpr', id: '' });
  const [res, setRes] = useState(null);
  useEffect(() => { apiGet('/user-management/users', { limit: 1000 }).then(d => setUsers(d.users || d.items || d || [])).catch(() => {}); }, []);
  const run = async () => {
    try { setRes(await apiGet('/approvals/explain', f)); } catch (e) { setRes(null); toast.error(e?.response?.data?.detail || 'Check failed'); }
  };
  const TYPES = ['mpr', 'material_requisition', 'po', 'work_order', 'vendor_empanelment', 'leave', 'regularization', 'attendance', 'travel_request', 'overtime', 'salary_advance', 'petty_cash', 'payment_voucher', 'vendor_invoice', 'permit_to_work', 'itp_approval', 'measurement_book', 'hire_log', 'reimbursement', 'mrf', 'user_approval'];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="ar-check-user">
      <div className="font-display font-bold text-sm mb-2">Check a user</div>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={f.user_id} onChange={e => setF({ ...f, user_id: e.target.value })} className="border border-slate-300 rounded-sm text-xs px-2 py-1 max-w-xs" data-testid="ar-check-user-select">
          <option value="">Pick a user…</option>
          {(Array.isArray(users) ? users : []).map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email} · {u.department || u.role}</option>)}
        </select>
        <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} className="border border-slate-300 rounded-sm text-xs px-2 py-1" data-testid="ar-check-type">
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={f.id} onChange={e => setF({ ...f, id: e.target.value })} placeholder="Document number or id" className="border border-slate-300 rounded-sm text-xs px-2 py-1 w-56" data-testid="ar-check-doc" />
        <button onClick={run} disabled={!f.user_id || !f.id} className="flex items-center gap-1 px-3 py-1 text-xs bg-[#3b4872] text-white rounded-sm disabled:opacity-50" data-testid="ar-check-run"><Search size={12} /> Check</button>
      </div>
      {res && (
        <div className={`mt-3 text-xs p-2 rounded-sm border ${res.can_act ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="ar-check-result">
          <b>{res.user}</b> {res.can_act ? `can act · basis: ${res.basis}` : `cannot act · ${res.reason_if_denied}`}
        </div>
      )}
    </div>
  );
}

function Report({ report }) {
  if (!report) return null;
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4 max-h-[480px] overflow-y-auto" data-testid="ar-who-report">
        <div className="font-display font-bold text-sm mb-2">Who can approve what</div>
        {report.rows.map(r => (
          <div key={r.type} className="py-1.5 border-t border-slate-100 text-xs">
            <b>{r.label}</b>
            {r.who.map((w, i) => <div key={i} className="ml-3 text-slate-600">• {w.label}{w.users.length ? `: ${w.users.slice(0, 12).join(', ')}${w.users.length > 12 ? ` +${w.users.length - 12}` : ''}` : ''}</div>)}
          </div>
        ))}
      </div>
      <div className="bg-white border border-amber-200 rounded-sm p-4 max-h-[480px] overflow-y-auto" data-testid="ar-no-pm-report">
        <div className="font-display font-bold text-sm mb-2 text-amber-800">Projects with no PM assigned ({report.projects_without_pm.length})</div>
        {report.projects_without_pm.map(p => <div key={p.id} className="text-xs py-0.5 text-slate-700">{p.name}{p.status ? ` · ${p.status}` : ''}</div>)}
      </div>
    </div>
  );
}

export function ApprovalRulesTab() {
  const [rules, setRules] = useState(null);
  const [depts, setDepts] = useState([]);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => {
    apiGet('/settings/approval-rules').then(setRules).catch(() => toast.error('Could not load approval rules'));
    apiGet('/approvals/rules/report').then(setReport).catch(() => {});
  };
  useEffect(() => {
    load();
    apiGet('/config-masters/departments').then(d => setDepts(d.values || [])).catch(() => {});
  }, []);
  if (!rules) return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  const save = async () => {
    setSaving(true);
    try { await apiPut('/settings/approval-rules', rules); toast.success('Approval rules saved'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  return (
    <div className="space-y-5 mt-6" data-testid="approval-rules-tab">
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-3 py-2">Approval type</th><th className="px-3 py-2">Approvers</th><th className="px-3 py-2">Teams / departments</th></tr>
          </thead>
          <tbody>
            {Object.entries(rules.rows).map(([k, row]) => (
              <RuleRow key={k} k={k} row={row} labels={rules.team_labels} depts={depts}
                onChange={r => setRules({ ...rules, rows: { ...rules.rows, [k]: r } })} />
            ))}
          </tbody>
        </table>
      </div>
      <TeamsCard rules={rules} setRules={setRules} labels={rules.team_labels} depts={depts} />
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm rounded-sm font-display font-semibold disabled:opacity-50" data-testid="ar-save-btn">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Approval Rules'}
        </button>
      </div>
      <CheckUser />
      <Report report={report} />
    </div>
  );
}
